import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface PromotionBanner {
  id: number;
  title: string;
  image: string;
  link: string;
  start_date: string;
  end_date: string;
  priority: number;
  status: string;
}

interface PromotionBannerContextType {
  banners: PromotionBanner[];
  loading: boolean;
  error: string | null;
  refetchBanners: () => Promise<void>;
  getBannerImage: (image?: string) => string;
}

const PromotionBannerContext = createContext<
  PromotionBannerContextType | undefined
>(undefined);

export function PromotionBannerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE_URL = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

  const [banners, setBanners] = useState<PromotionBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getBannerImage = useCallback(
    (image?: string) => {
      if (!image) return "";
      return `${IMAGE_URL}/uploads/promotions/${image}`;
    },
    [IMAGE_URL],
  );

  const refetchBanners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Change this endpoint only if your public website route is different
      const res = await fetch(`${BASE_URL}/admin/settings/banners/active`);

      const result = await res.json();

      if (result?.success) {
        const sorted = (result.data || []).sort(
          (a: PromotionBanner, b: PromotionBanner) =>
            Number(a.priority || 0) - Number(b.priority || 0),
        );

        setBanners(sorted);
      } else {
        setBanners([]);
        setError(result?.message || "Unable to fetch promotion banners.");
      }
    } catch (err) {
      console.error("Promotion banner fetch error:", err);
      setBanners([]);
      setError("Something went wrong while fetching promotion banners.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    refetchBanners();
  }, [refetchBanners]);

  const value = useMemo(
    () => ({
      banners,
      loading,
      error,
      refetchBanners,
      getBannerImage,
    }),
    [banners, loading, error, refetchBanners, getBannerImage],
  );

  return (
    <PromotionBannerContext.Provider value={value}>
      {children}
    </PromotionBannerContext.Provider>
  );
}

export function usePromotionBanners() {
  const context = useContext(PromotionBannerContext);

  if (!context) {
    throw new Error(
      "usePromotionBanners must be used within a PromotionBannerProvider",
    );
  }

  return context;
}